<template>
  <div class="salaries-list-page">

    <!-- FILTERS WRAPPER -->
    <div class="salaries-list-page__filters-wrapper">

      <!-- FILTERS -->
      <b-card
        class="salaries-list-page__filters"
        body-class="d-flex justify-content-space-between align-items-flex-start gap-10 p-1"
      >

        <!-- Filters / search : first column -->
        <FiltersSearch
          :class="[
            (isConsumerPending || isCandyBarPending) ? 'salaries-list-page__filters--busy' : '',
            'd-flex flex-grow-1 flex-wrap flex-basis-0'
          ]"
          v-bind="{
            search: context.search,
            filters: context.filters,
            updateSearchCallback: setSearch,
            resetSearchCallback: resetSearch,
            updateFiltersCallback: setFilters,
            resetFiltersCallback: resetCriterion,
          }"
        />

        <!-- Filter: second column -->
        <ui-wad class="d-flex flex-shrink-0">
          <ui-popover-wrapper
            ref="filtersCreatePopover"
            size="md"
            click
            no-flip
            no-caret
            placement="bottom-end"
            body-class="p-1"
          >
            <template #default>
              <layout-cta-sub
                component="ui-link-button"
                :variant="UI.VARIANT.PRIMARY"
                :size="UI.SIZE.SMALL"
              >
                <ui-icon name="add" />
                &nbsp;Ajouter un salarié
              </layout-cta-sub>
            </template>
            <template #popover>
              <div class="salaries-list-page__cta-popover">
                <div
                  class="salaries-list-page__cta-popover__button"
                >
                  <ui-wad class="text-bold">Avec demande d'état civil</ui-wad>
                  <p class="color-gray-dark">Le salarié renseigne lui-même son état civil et dépose ses documents</p>
                </div>
                  <div
                    class="salaries-list-page__cta-popover__button"
                    @click="onManualSelected"
                  >
                  <ui-wad class="text-bold">Saisir moi-même la fiche</ui-wad>
                  <p class="color-gray-dark">Aucune sollicitation envoyée au salarié</p>
                </div>
              </div>
            </template>
          </ui-popover-wrapper>
        </ui-wad>
      </b-card>
    </div>

    <div class="salaries-list-page__main">

      <!-- TABLE -->
      <div
        class="salaries-list-page__table-scroll"
        :class="{ 'salaries-list-page__table-scroll--busy': isForcedBusy }"
      >
        <ui-table-wrapper
          ref="table"
          class="salaries-list-page__table-main"
          :provider="provider"
          :fields="fields"
          primary-key="id"
          :tbody-tr-class="tbodyRowClass"
          :size="UI.SIZE.LARGE"
          :hide-header="providerCallCount === 0"
          hover
          striped
          fixed
          :initial-sort-by="context.order"
          :initial-sort-desc="context.sort === 'desc'"
          @row-dblclicked="({ item }) => editSalarie(item)"
        >
          <template
            v-if="providerCallCount === 0"
            #table-busy
          >
            <layout-placeholder-table-body
              :count="context.perPage"
              :size="UI.SIZE.SMALL"
              striped
              class="salaries-list-page__placeholder"
            />
          </template>

          <template #empty>
            <div class="salaries-list-page__empty">
              <ui-icon
                name="search_off"
                class="font-size-40px color-black mb-1"
              />
              <p class="font-weight-600 color-black mb-0.25 font-family-heading">
                Aucun résultat trouvé
              </p>
              <p class="mb-2">
                Nous n'avons trouvé aucun salarié correspondant à votre recherche.
              </p>
              <ui-button
                v-if="hasAnyCriterionSet"
                :variant="UI.VARIANT.LIGHT"
                :size="UI.SIZE.SMALL"
                @click="resetCriterion"
              >
                <ui-icon
                  name="replay"
                />&nbsp;Réinitialiser les filtres
              </ui-button>
            </div>
          </template>

          <template
            v-if="
              (
                isHolisticSuggestionDisplayed ||
                isHolisticSelectionEnabled
              )
            "
            #top-row="{ columns }"
          >
            <ui-table-top-row-selection
              :suggestion-displayed="isHolisticSuggestionDisplayed"
              :selection-displayed="isHolisticSelectionEnabled"
              :selected-count="bufferedSelectedItemsCount"
              :total-count="context.total"
              :colspan="columns"
              class="salaries-list-page__row-selection"
              @unselect="resetSelection"
              @select="setHolisticSelection"
            />
          </template>

          <template #head(selection)>
            <ui-button-icon
              :status="
                providerCallCount === 0 ? UI.STATUS.MUTED : UI.STATUS.ENABLED
              "
              :size="UI.SIZE.SMALL"
              :overlay-size="UI.SIZE.LARGE"
              :class="checkboxAllIconClassname"
              :name="checkboxAllIconName"
              :theme="UI.ICON_THEME.FILLED"
              @click="toggleSelectAll"
            />
          </template>

          <template #cell(selection)="data">
            <ui-button-icon
              :size="UI.SIZE.SMALL"
              :overlay-size="UI.SIZE.LARGE"
              :variant="UI.VARIANT.DARK"
              :name="isItemSelected(data.item) ?
                'check_box' :
                'check_box_outline_blank'
              "
              :theme="UI.ICON_THEME.FILLED"
              :class="
                isItemSelected(data.item) ?
                  'color-primary' :
                  'color-gray-dark'
              "
              @click="toggleItem(data.item)"
            />
          </template>

          <template #cell(etat_civil)="{ item }">

            <ui-popover-wrapper
              v-if="item.etat_civil"
              :size="UI.SIZE.SMALL"
              placement="bottom"
              no-animation
              :delay="100"
              class="cursor-help"
            >
              <template #popover>
                <EtatCivilPopover :etat-civil="item.etat_civil"/>
              </template>

              <div>
                <ui-icon
                  name="circle"
                  :theme="UI.ICON_THEME.FILLED"
                  :class="`salaries-list-page__etat-civil--${ETAT_CIVIL_STATUS_TO_LABEL[item.etat_civil.statut]}`"
                  class="salaries-list-page__etat-civil"
                />
              </div>

            </ui-popover-wrapper>
          </template>

          <template #cell(taux_pas)="{ item }">
            <TauxPas
              :taux-pas="item.taux_pas"
            />
          </template>

          <template #cell(documents)="{ item }">
            <Documents :documents="item.documents"/>
          </template>

        </ui-table-wrapper>
      </div>

      <!-- FOOTER -->
      <div
        v-if="providerCallCount !== 0 && context.total > 0"
        class="salaries-list-page__footer"
      >

        <span
          v-hide="context.total === 0"
          class="salaries-list-page__footer__count"
        >
          <layout-misc-float :value="context.total" />
          {{ context.total <= 1 ? 'résultat' : 'résultats' }}
        </span>

        <ui-blank
          v-if="context.total === 0"
          class="salaries-list-page__footer__blank"
        />

        <ui-table-pagination
          v-model="context.currentPage"
          v-hide="context.total < context.perPage || context.total === 0"
          :total-rows="context.total"
          :per-page="context.perPage"
        />

        <div class="salaries-list-page__footer__per-page">
          <ui-form-select
            v-model="context.perPage"
            :size="UI.SIZE.SMALL"
            :options="perPageOptions"
            class="salaries-list-page__footer__per-page__select"
          />
          <div class="salaries-list-page__footer__per-page__text">
            par page
          </div>
        </div>

      </div>

      <!-- CANDY BAR -->
      <CandyBarAndModal
        :selection-count="selectionCount"
        :has-any-item-selected="hasAnyItemSelected"
        :buffered-selected-items="bufferedSelectedItems"
        :context="context"
        :refresh-table-callback="refreshTable"
        :reset-selection-callback="resetSelection"
        :available-ec-salarie="availableEcSalarie"
        :unavailable-ec-salarie="unavailableEcSalarie"
        :set-candy-bar-pending="setCandyBarPending"
        :set-forced-busy="setForcedBusy"
      />
    </div>
  </div>

  <!-- Salaries creation modal -->
  <ui-modal
    :id="SALARIES_CREATE_MODAL_ID"
    :size="UI.SIZE.SMALL"
    no-header
    no-footer
    no-outside-close
    :modal-class="[
      'salaries-list-page__modal',
    ]"
  >
    <manual-salarie-creation
      v-if="mode === MODE.MANUAL_FORM"
      ref="ManualSalarieCreation"
      :submit-callback="submitSalarie"
      @cancel="() => hideModal(SALARIES_CREATE_MODAL_ID)"
      @close="() => hideModal(SALARIES_CREATE_MODAL_ID)"
    />
  </ui-modal>

  <!-- NO DATA MODAL-->
  <ui-modal
    :id="NO_DATA_PAGE"
    fit-workspace
    no-header
    no-footer
  >
    <div class="salaries-list-page__no-data ">
      <div class="salaries-list-page__no-data__adding-bloc">
        <ui-icon
          name="person_add_alt"
          class="salaries-list-page__no-data__adding-bloc__icon"
        />
        <span class="salaries-list-page__no-data__adding-bloc__text">
          Aucun salarié dans cette {{ current_societe_ids?.length == 1 ? `société` : `structure` }}
        </span>
        <ui-popover-wrapper
          ref="noDataCreatePopover"
          size="md"
          click
          no-flip
          no-caret
          placement="bottom"
          body-class="p-1"
          teleport-disabled
        >
          <template #default>
            <layout-cta-sub
              component="ui-link-button"
              :variant="UI.VARIANT.PRIMARY"
              :size="UI.SIZE.SMALL"
              class="font-size-15px"
            >
              <ui-icon name="add" />
              &nbsp;Ajouter un salarié
            </layout-cta-sub>
          </template>
          <template #popover>
            <div class="salaries-list-page__cta-popover">
              <div class="salaries-list-page__cta-popover__button">
                <ui-wad class="text-bold">Avec demande d'état civil</ui-wad>
                <p class="color-gray-dark">Le salarié renseigne lui-même son état civil et dépose ses documents</p>
              </div>
              <div class="salaries-list-page__cta-popover__button" @click="onManualSelected">
                <ui-wad class="text-bold">Saisir moi-même la fiche</ui-wad>
                <p class="color-gray-dark">Aucune sollicitation envoyée au salarié</p>
              </div>
            </div>
          </template>
        </ui-popover-wrapper>
      </div>

      <img
        :src="'/images/drawings/Gestion-Sociale1-sans-texte.png'"
        class="salaries-list-page__no-data__img"
      >
    </div>
  </ui-modal>
</template>

<script lang="ts">
import { useTableProvider, type Config, type DefaultData as ProviderDefaultData } from '~/composables/layout/table-provider'
import { useAccessControl } from '~/composables/layout/access-control';
import { useCompanyOptionalNavigationContext } from '~/composables/layout/navigation';
import { type UiPopoverWrapper, type UiTableWrapper } from '#components';
import type { ComponentExposed } from 'vue-component-type-helpers'
import { equals, pipe_, pluck, prop, toString, singleton, tapCatch, values, noop, delay } from '~/utils/lib/core/prelude';
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types';
import { wrap as wrapInput } from '~/utils/infra/common/types';
import type { TableField } from '~/components/ui/table/Wrapper.vue'
import { useToastBuilder } from '~/composables/layout/toast';
import Console from '~/utils/lib/core/console'
import { getDetailsRoute } from '~/stores/salaries/layout';
import { useLegacyModalController } from '~/composables/layout/modal';
import { CIVILITE_TO_GENRE, ETAT_CIVIL_STATUS_TO_LABEL, FISCALITE_FORM_TO_API, SalarieTauxPasStatus, type SalarieTypesDocument, TYPE_CONTRAT_TO_LABEL, type TypeContrat } from '~/stores/salaries';
import EtatCivilPopover from '~/components/salaries/list/EtatCivilPopover.vue';
import TauxPas from '~/components/salaries/list/TauxPas.vue';
import FiltersSearch from '~/components/salaries/list/FiltersSearch.vue';
import ManualSalarieCreation from '~/components/salaries/list/ManualSalarieCreation.vue'
import { getDefaultFilters, useStore_salaries_list, type SalarieInList, type SalariesListUiFilters } from '~/stores/salaries/list';
import { useTableSelectionMultipleHolistic } from '~/composables/layout/table-selection-multiple-holistic'
import { useStore_salaries_details, type CreateSalariePayload } from '~/stores/salaries/details';
import { parseDatetime } from '~/utils/lib/core/datetime'
import { useStore_prefligh_salaries } from '~/stores/salaries/preflight';
import Documents from '~/components/salaries/list/Documents.vue';
import CandyBarAndModal from '~/components/salaries/list/CandyBarAndModal.vue';

const console = new Console('page:salaries-list')

type MODE = typeof MODE[keyof typeof MODE]
const MODE = {
  LIGHT_FORM: 'light-form',
  MANUAL_FORM: 'manual-form',
} as const

const SALARIES_CREATE_MODAL_ID = 'salarie-create-modal-id'

export default defineNuxtComponent({
  components: {
    EtatCivilPopover,
    TauxPas,
    FiltersSearch,
    ManualSalarieCreation,
    Documents,
    CandyBarAndModal
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_OPTIONAL,
      menuItem: MENU_ITEM.SALARIES
    })

    const navigationContext = useCompanyOptionalNavigationContext()
    const ref_table = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')
    const ref_filtersCreatePopover
      = useTemplateRef<InstanceType<typeof UiPopoverWrapper>>('filtersCreatePopover')
    const ref_noDataCreatePopover
      = useTemplateRef<InstanceType<typeof UiPopoverWrapper>>('noDataCreatePopover')
    const store_salaries_list = useStore_salaries_list()
    const store_salaries_details = useStore_salaries_details()
    const store_salaries_preflight_etat_civil = useStore_prefligh_salaries()
    const ToastBuilder = useToastBuilder()

    const providerDefaultData :ProviderDefaultData = {
      context: {
        perPage: 50,
        filters: getDefaultFilters(),
        order: "nom",
        sort: "asc"
      }
    }

    const config :Config = {
      precedingSorting: {
        sort: 'asc',
        order: 'nom',
      },
      search: {
        minChars: 2,
      },
    }

    const tableProvider = useTableProvider<SalarieInList, SalariesListUiFilters>(
      ref_table,
      providerDefaultData,
      config
    )

    const tableSelectionMultiple = useTableSelectionMultipleHolistic(
      tableProvider.currentItems,
      (item: SalarieInList, item_: SalarieInList) => {
        const id  = prop('id', item)
        const id_ = prop('id', item_)
        return equals(id, id_)
      },
      tableProvider.context,
    )

    const current_societe_ids = (() => {
      const currentLevel = navigationContext.fromCurrentLevel()

      switch (currentLevel.kind) {

        case 'COMPANY':
          return singleton(currentLevel.company.societeId)

        case 'STRUCTURE':
          return pipe_(
            values(currentLevel.structure.societes),
            pluck('societeId')
          )
      }
    })()

    const {
      hideModal,
      showModal,
    } = useLegacyModalController()

    return {
      ...navigationContext,
      ...useAccessControl(),
      ...tableProvider,
      ...tableSelectionMultiple,
      store_salaries_list,
      store_salaries_details,
      store_salaries_preflight_etat_civil,
      ToastBuilder,
      current_societe_ids,
      ETAT_CIVIL_STATUS_TO_LABEL,
      SalarieTauxPasStatus,
      TYPE_CONTRAT_TO_LABEL,
      hideModal,
      showModal,
      ref_filtersCreatePopover,
      ref_noDataCreatePopover,
    }
  },
  data() {
    const NO_DATA_PAGE = "salaries-index-id"

    return {
      NO_DATA_PAGE,
      SALARIES_CREATE_MODAL_ID,
      MODE,
      mode: MODE.MANUAL_FORM as MODE,
      unavailableEcSalarie: [] as SalarieInList[],
      availableEcSalarie: [] as SalarieInList[],
      unavailableTauxPasCount: 0 as number, //integer
      availableTauxPasCount: 0 as number, //integer
      isForcedBusy: false,
      isCandyBarPending: false,
      fields: [
      {
          key: 'selection',
          label: '',
          class: 'salaries-list-page__selection',
        },
        {
          key: 'code_societe',
          label: 'Code soc.',
          class: 'salaries-list-page__code-societe',
          displayed: this.current_societe_ids?.length == 1 ? false : true,
          manageable: true,
        },
        {
          key: 'matricule',
          label: 'Matricule',
          class: 'salaries-list-page__matricule',
          sortable: true
        },
        {
          key: 'nom',
          label: 'Nom',
          tdClass: 'font-weight-bold',
          class: 'salaries-list-page__salarie',
          sortable: true,
        },
        {
          key: 'prenom',
          label: 'Prénom',
          class: 'salaries-list-page__salarie',
          sortable: true
        },
        {
          key: 'etat_civil',
          label: 'État civil',
          class: 'salaries-list-page__etat-civil',
          sortable: true
        },
        {
          key: 'taux_pas',
          label: 'Taux PAS',
          class: 'salaries-list-page__taux-pas',
        },
        {
          key: 'documents',
          label: 'Documents',
          class: 'salaries-list-page__documents',
        },
        {
          key: 'type_contrat',
          label: 'CTT',
          class: 'salaries-list-page__type-contrat',
          formatter: ({value}: { value?: TypeContrat }) => {
            if (value === undefined)
              return '—'
            return TYPE_CONTRAT_TO_LABEL[value]
          }
        },
        {
          key: 'libelle_profession',
          label: 'Dernière profession',
          class: 'salaries-list-page__libelle-profession',
          formatter: ({value}) => {
            if (value === undefined)
              return '—'
            return value
          }
        },
      ] as TableField[],
      perPageOptions: [
        { value: 50, text: '50' },
        { value: 100, text: '100' },
        { value: 300, text: '300' },
      ],
    }
  },
  computed: {
    openNoDataModal(): boolean {
      return this.providerCallCount === 1
        && this.context.total === 0
        && !this.hasAnyCriterionSet
    },
    selectionCount(): number {
      return this.selectionType === 'all'
        ? this.context.total
        : this.bufferedSelectedItemsCount
    },
  },
  watch: {
    openNoDataModal(value) {
      if (value)
        this.showModal(this.NO_DATA_PAGE)
    },
    'context.search'() {
      this.resetSelection()
    },
    'context.filters'() {
      this.resetSelection()
    },
    'context.currentPage'() {
      this.resetSelection()
    },
    bufferedSelectedItems() {
      if (this.bufferedSelectedItemsCount <= 20)
        this.checkEmailSelectionCurrentPage()
    }
  },
  created() {
    this.consumer = (context) => {
      const { structure: { uuidData } } = this.fromCurrentLevel()
      const input = wrapInput({
        ...context,
        filters: {
          ...context.filters,
          search: context.search,
          societe_ids: this.current_societe_ids,
        },
      })

      return this.store_salaries_list.list({
        uuidData,
        input,
      })
        .catch(tapCatch(err => {
          console.warn('list', err)
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la récupération des salariés')
            .show()
        }))
    }
  },
  methods: {
    onManualSelected() {
      this.ref_filtersCreatePopover?.hide()
      this.ref_noDataCreatePopover?.hide()
      this.mode = MODE.MANUAL_FORM
      this.showModal(this.SALARIES_CREATE_MODAL_ID)
    },
    editSalarie(item: SalarieInList) {
      if (!this.hasFeature(this.FEATURES.ENG_399_DISABLE_CHECKS)) return

      const currentLevel = this.fromCurrentLevel()

      return navigateTo(
        getDetailsRoute(
          currentLevel.structure.id,
          item.societe_id,
          item.id,
          currentLevel.kind === "COMPANY" ? currentLevel.company.code_societe : ''
        )
      )
    },
    checkEmailSelectionCurrentPage() {
      const result = this.store_salaries_preflight_etat_civil
        .doPreflightPartition(this.bufferedSelectedItems)

      this.availableEcSalarie = result.email
      this.unavailableEcSalarie = result.noEmail
    },
    setCandyBarPending(pending: boolean) {
      this.isCandyBarPending = pending
    },
    setForcedBusy(refresh: boolean) {
      this.isForcedBusy = refresh
    },
    submitSalarie(data: any) {
      const { structure: { uuidData } } = this.fromCurrentLevel()
      const societeId = data.company

      const payload: CreateSalariePayload = {
        civilite: CIVILITE_TO_GENRE[data.titre],
        nom: data.nom,
        prenom: data.prenom,
        code_pays_nationalite: data.code_pays_nationalite,
        mail: data.mail,
        pseudonyme: data.pseudonyme,
        numero_securite_sociale: data.numero_securite_sociale,
        telephone: data.telephone,
        adresse: data.adresse,
        complement_adresse: data.complement_adresse,
        pays_fiscalite: FISCALITE_FORM_TO_API[data.pays_fiscalite],
        code_postal: data.code_postal,
        ville: data.ville,
        code_pays: data.code_pays,
        numero_conges_spectacles: data.numero_conges_spectacles,
        naissance: {
          nom: data.nom_naissance,
          date: data.date_naissance
            ? parseDatetime({ outputFormat: 'YYYY-MM-DD' }, data.date_naissance)
            : '',
          lieu: data.lieu_naissance,
          departement: data.departement_naissance,
          code_pays: data.code_pays_naissance,
        },
        banque: {
          iban: data.IBAN,
          bic: data.BIC,
        },
        titreSejour: {
          numero: data.titre_sejour_numero,
          finValidite: data.titre_sejour_fin_validite,
        },
        visiteMedicale: {
          date: data.date_visite_medicale
            ? parseDatetime({ outputFormat: 'YYYY-MM-DD' }, data.date_visite_medicale)
            : '',
          prochaineDate: data.date_prochaine_visite_medicale
            ? parseDatetime({ outputFormat: 'YYYY-MM-DD' }, data.date_prochaine_visite_medicale)
            : '',
        },
      }

      return this.store_salaries_details.create({ uuidData, societeId, data: payload })
        .then(() => {
          this.hideModal(SALARIES_CREATE_MODAL_ID)

          this.ToastBuilder
            .success('Le salarié a bien été créé')
            .title('Salarié créé')
            .show()

          this.refreshTable()
        }
        )
        .catch(tapCatch(err => {
          this.ToastBuilder
            .error(toString(err))
            .title('Erreur lors de la création du salarié')
            .show()
        }))
    },
  },
})

</script>

<style lang="sass">
@import '~/assets/sass/modules/layout/members'

.salaries-list-page__modal
  .modal-dialog
    max-width: map-get($breakpoints, 'tablet')

  @include media('<tablet')
    @include fit-modal
</style>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/members'
@import '~/assets/sass/modules/table/members'

// skeuomorphism
$etat-civil-status-color: ("AR": $warning, "INVALID": $danger, "VALID": $success)

$font-size-text-no-data: 18px
$font-size-icon-no-data: 40px
$selection-column-width: 50px
$code-soc-column-width: 90px
$matricule-column-width: 100px
$salarie-column-width: 200px
$etat-civil-column-width: 90px
$taux-pas-column-width: 100px
$type-contrat-column-width: 50px
$libelle-profession-column-width: 250px
$documents-column-width: 80px

$cumul-row-height: 37px

.salaries-list-page
  $self: &

  height: 100%
  display: flex
  flex-direction: column

  &__no-data
    @include inset
    position: relative
    display: flex
    height: 100%
    flex-grow: 1
    flex-direction: row
    opacity: 0
    animation: fade-in 800ms ease-in 300ms forwards

    &__adding-bloc
      display: flex
      flex-direction: column
      align-items: center
      margin: 30vh auto 0
      font-size: $font-size-text-no-data

      &__icon
        color: $primary
        font-size: $font-size-icon-no-data

      &__text
        font-weight: $font-weight-bold
        margin-bottom: space-x(1.5)
        margin-top: space-x(0.25)

    &__img
      position: absolute
      width: 30vw
      height: auto
      bottom: 20%
      left: 50%
      transform: translateX(-50%)
      object-fit: contain
      filter: grayscale(0.2) opacity(0.075)
      z-index: -10

  &__cta-popover
    display: flex
    flex-direction: column
    gap: space-x(1)

    &__button
      cursor: pointer
      transition: $transition-base
      padding: space-x(1)
      border-radius: $border-radius-sm

      &:hover
        background-color: mix-alpha($primary, 0.1)

  &__filters-wrapper
    background-color: $background-primary
    padding: space-x(3) space-x(3.5)

  &__main
    height: 100%
    display: flex
    flex-direction: column

  &__table-scroll
    @include always-show-scrollbar()
    contain: size
    overflow-x: scroll
    height: 100%
    flex-grow: 1
    scroll-padding-bottom: $cumul-row-height
    container-type: inline-size

    &--busy
      :deep(tbody)
        opacity: 0.4
        transition: 0.3s


  ///////////////////////////////////////

  &__filters

    &--busy
      @include no-interaction
      opacity: 0.6
      transition: 0.3s
      transition-delay: 0.3s

  ///////////////////////////////////////

  &__row-selection

    :deep(.ui-table-top-row-selection__inner)
      width: 100cqw

  ///////////////////////////////////////

  &__empty
    position: sticky
    left: 0
    width: 100cqw
    display: flex
    flex-direction: column
    align-items: center
    margin: 20vh 0 space-x(2)

  ///////////////////////////////////////

  &__footer
    border-top: 1px solid $border-color
    display: flex
    align-items: center
    justify-content: space-between
    padding: ($layout-spacer-y * .25) ($layout-spacer-x * .5)

    &__count
      font-size: 85%

    &__per-page
      display: flex
      align-items: center
      gap: space-x(1)
      flex-shrink: 0

      &__text
        font-weight: $font-weight-light
        font-size: 90%
        white-space: nowrap

  ///////////////////////////////

  :deep(.b-table.table)
    @include table-presets
    @include table-body-grow

    margin-bottom: 0
    height: 100%

    /// Header chrome, needed on both nodes:
    ///   - `thead`: outlives the `hide-header` state (see <ui-table-wrapper>,
    ///     `visibility` suppressing the element own painting)
    ///   - `th`: travels with the sticky pinning (see below)
    $header-background: $body-bg
    $header-rule: inset 0 -1px 0 $submenu-border-color

    thead
      background-color: $header-background
      box-shadow: $header-rule

    @include table-header-small
      background-color: $header-background
      box-shadow: $header-rule
      font-size: 13.5px

    #{ $self }__selection
      width: $selection-column-width
      text-align: center

    #{ $self }__code-societe
      width: $code-soc-column-width

    #{ $self }__matricule
      width: $matricule-column-width

    #{ $self }__salarie
      max-width: 100%
      width: $salarie-column-width

    #{ $self }__etat-civil
      width: $etat-civil-column-width
      text-align: center

      @each $status, $color in $etat-civil-status-color
        &--#{$status}
          @include neon($color)

    #{ $self }__taux-pas
      width: $taux-pas-column-width
      text-align: center

    #{ $self }__documents
      width: $documents-column-width
      text-align: center

    #{ $self }__type-contrat
      width: $type-contrat-column-width
      text-align: center

    #{ $self }__libelle-profession
      width: $libelle-profession-column-width

  ////////////////////////////////////

  /// Sticky/fixed blocks
  ///
  /// (note that "boostrap-vue-next" ":sticky-header" prop is used to make
  /// the header sticky via an additional inner scroll → we already have such
  /// a container, see "__table-scroll", hence the ad hoc override)

  :deep(.b-table.table)

    thead th
      position: sticky
      top: 0
      z-index: z-index($z-indexes, 'main', 'ceil')

    tfoot
      background-color: map-get($palette-layout, 'color-3')
      position: sticky
      bottom: 0
      z-index: z-index($z-indexes, 'main', 'ceil')

    tfoot tr.satd-saisies-list-page__cumul-row td
      gap: 0
      color: $white
      font-weight: $font-weight-normal
      overflow: visible
      text-align: right
      font-size: 14px
      transition: opacity 200ms ease

    tfoot tr.satd-saisies-list-page__cumul-row--loading td:not(:first-child)
      opacity: 0.5

  ////////////////////////////////////

  /// Animation
  ///

  &__filters
    animation: fade-in 150ms ease-in-out

  &__empty
    animation: fade-in 50ms ease-in

  /// Grace delay before showing the loading rows: a fast enough consumer
  /// unmounts them before they ever get visible (avoiding a flicker whenever
  /// the result set is systematically empty)
  &__placeholder
    opacity: 0
    animation: fade-in 300ms ease-in 150ms forwards

</style>
